import { ReactElement } from 'react';
import { Modal } from '@weareopenr/component-library';
import { FieldValues } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { usePublishMenu } from 'src/hooks/usePublishMenu/usePublishMenu';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { IPublishMenusProps } from 'src/api/publishMenu/types';
import { IMenu } from 'src/types/menu.types';
import { useExternalSystemPublishing } from '../../hooks/useExternalSystemPublishing';
import PublishSetupView from './PublishSetupView';
import SummaryView from './SummaryView/SummaryView';
import { IPublishContext, PublishFlow, PublishFlowGate } from './PublishFlow/PublishFlow';
import filterMealtimesWithCategories from './utilities/filterMealtimesWithCategories';
import ModalImg from '../../assets/images/chef-plating.png';
import { IModalProps, IPublishDestination } from './types';

const MenuPublishFlow = ({
  menuId,
  isTrial,
  closeModal,
  publicationData,
  menu,
}: {
  menuId: string;
  isTrial: boolean;
  publicationData: IPublishDestination[];
  menu: IMenu;
  closeModal: () => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { trackEvent } = useUserTracking();
  const publishMenu = usePublishMenu(menuId);
  const validate = useValidate();

  const showSchedule = !isTrial;

  const defaultVenueIds = Array.from(
    new Set(
      publicationData
        .flatMap((channel) => channel.venues)
        .filter((venue) => menu.linkedVenueIds.has(venue.venue_id))
        .map((venue) => venue.venue_id),
    ),
  );

  const filteredMealtimes = filterMealtimesWithCategories(menu.mealtimes);

  const channelNames = new Map(publicationData.map(({ channel_id, name }) => [channel_id, name] as const));

  const handlePublish = async (
    values: FieldValues,
    { publicationDateTime, isScheduled, notifySuccess }: IPublishContext,
  ) => {
    const selectedChannelSet = new Set<string>(values['selectedChannels'] ?? []);
    const selectedVenueIds = new Set((values['venues'] ?? []) as string[]);

    const channels: IPublishMenusProps[] = publicationData
      .filter((channel) => selectedChannelSet.has(channel.channel_id))
      .map((channel) => ({
        channel_id: channel.channel_id,
        venues: channel.venues.map((venue) => venue.venue_id).filter((venueId) => selectedVenueIds.has(venueId)),
      }))
      .filter((channel) => channel.venues.length > 0);

    const names = channels.flatMap(({ channel_id }) => (channel_id && channelNames.get(channel_id)) || []);

    const response = await publishMenu.mutateAsync({
      price_scheme_id: values['price-scheme'],
      use_mealtimes: values['mealtimes'] === 'enabled',
      ...(publicationDateTime && {
        publication_date_time: new Date(publicationDateTime).toISOString(),
        schedule_name: values['scheduleName'],
      }),
      channels,
    });

    // Validation only applies to scheduled menu publishes - publishing
    // immediately has no schedule to attach a report to. This fires
    // fire-and-forget: the report is viewed later from the schedule card,
    // not blocking the publish success flow here.
    if (isScheduled && response?.schedule_id) {
      validate.mutate({
        criteria: {
          schedule_id: response.schedule_id,
          price_scheme_id: values['price-scheme'],
          menu_id: menuId,
          rules: getValidationRules(EVENT_TYPES.PUBLISH_MENU),
        },
      });
    }

    trackEvent('menu_published', {
      menuId,
      menuName: menu.name,
      channelCount: channels.length,
      useMealtimes: values['mealtimes'] === 'enabled',
      isScheduled,
    });

    notifySuccess(names);
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    await new Promise((resolve) => setTimeout(resolve, 1000));
    if (isScheduled) {
      closeModal();
    } else {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate(`/menus/${menuId}/status`);
    }
  };

  return (
    <PublishFlow
      entityName={menu.name}
      publicationData={publicationData}
      defaultVenueIds={defaultVenueIds}
      defaultValues={{ mealtimes: 'disabled' }}
      requirePriceScheme
      showSchedule={showSchedule}
      closeModal={closeModal}
      renderSetup={(form) => (
        <PublishSetupView
          {...form}
          menuName={menu.name}
          mealtimes={menu.mealtimes}
          filteredMealtimes={filteredMealtimes}
          publicationData={publicationData}
          showSchedule={showSchedule}
        />
      )}
      renderSummary={({ watch }) => (
        <SummaryView
          watch={watch}
          menuName={menu.name}
          publicationData={publicationData}
          filteredMealtimes={filteredMealtimes}
          isMealtimeOptionEnabled={watch('mealtimes') === 'enabled'}
        />
      )}
      onPublish={handlePublish}
      errorTags={{ action: 'publish_menu', menuId }}
    />
  );
};

const PublishFlowWrapper = ({
  menuId,
  isTrial,
  closeModal,
}: {
  menuId: string;
  isTrial: boolean;
  closeModal: () => void;
}) => {
  const { data: menu, isLoading: isMenuLoading } = useMenu(menuId);
  const {
    data: publicationData,
    isLoading: isPublicationDataLoading,
    isError,
  } = useExternalSystemPublishing(isTrial, menu?.publication);

  return (
    <PublishFlowGate
      isLoading={isMenuLoading || isPublicationDataLoading}
      isUnavailable={!publicationData || publicationData.length === 0 || isError}
    >
      <MenuPublishFlow
        menuId={menuId}
        isTrial={isTrial}
        closeModal={closeModal}
        publicationData={publicationData as IPublishDestination[]}
        menu={menu as IMenu}
      />
    </PublishFlowGate>
  );
};

const PublishModal = ({ closeModal, isOpen, menuId, isTrial = false }: IModalProps): ReactElement => {
  return (
    <Modal closeModal={closeModal} open={isOpen} image={ModalImg} imageAlt="Picture of a chef plating up">
      <PublishFlowWrapper menuId={menuId} isTrial={isTrial} closeModal={closeModal} />
    </Modal>
  );
};

export default PublishModal;
